import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { Doughnut } from 'react-chartjs-2';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { CustomTooltip, ConditionalWrapper } from 'ui';

import { theme } from 'theme';
import {
  worstColor,
  averageColor,
  bestColor,
  missingColor,
} from 'shared/constants/ESGScoresColors';
import { getHexColorByScoreOverview } from 'shared/functions';
import { useReportPage } from 'shared/hooks';

const useStyles = makeStyles(themeObject => ({
  chartWrapper: {
    width: useReportPage ? 'auto' : 260,
    position: 'relative',
    margin: '0 auto',

    '& .chartjs-size-monitor': {
      maxWidth: 0,
    },
    '& .chartjs-render-monitor': {
      margin: '0 auto',
    },
  },
  scoreNumbers: {
    position: 'absolute',
    top: useReportPage ? '90%' : '85%',
    left: '50%',
    transform: `translate(-50%, -50%) ${useReportPage ? 'scale(0.6)' : ''}`,
  },
  scoreNumber: {
    fontSize: 40,
    fontWeight: 500,
    display: 'inline-block',
  },
  scoreMaxNumber: {
    fontSize: 18,
    fontWeight: 500,
    display: 'inline-block',
  },
  legendChip: {
    borderRadius: 10,
    width: 20,
    height: 10,
    background: 'red',
  },
  tooltipTitle: {
    color: themeObject.palette.primary.main,
    fontWeight: 500,
  },
}));

const prepareDataToDisplay = value => parseFloat(value.toFixed(1));

const ESGScore = ({ title, score, worst, average, best }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const isComparison = worst || average || best;

  const notFilledColor = isComparison ? 'transparent' : missingColor;

  const maxScore = 10;

  const data = {
    datasets: [
      ...(!isComparison
        ? [
            {
              data: [score, maxScore - score],
              backgroundColor: [getHexColorByScoreOverview(score), notFilledColor],
            },
          ]
        : []),
      ...(worst !== null
        ? [
            {
              data: [worst, maxScore - worst],
              backgroundColor: [worstColor, notFilledColor],
            },
          ]
        : []),
      ...(average !== null
        ? [
            {
              data: [average, maxScore - average],
              backgroundColor: [averageColor, notFilledColor],
            },
          ]
        : []),
      ...(best !== null
        ? [
            {
              data: [best, maxScore - best],
              backgroundColor: [bestColor, notFilledColor],
            },
          ]
        : []),
    ],
  };

  const options = {
    cutout: '85%',
    circumference: 180,
    rotation: -90,
    width: 250,
    aspectRatio: 2,
    legend: {
      display: false,
    },
    tooltips: {
      enabled: false,
    },
    hover: {
      mode: null,
    },
    elements: {
      arc: {
        borderWidth: 0,
      },
    },
  };

  return (
    <>
      {title && (
        <Box mb={3}>
          <Typography align="center" variant="h5">
            <strong>{title}</strong>
          </Typography>
        </Box>
      )}
      <div className={classes.chartWrapper}>
        <ConditionalWrapper
          condition={isComparison}
          wrapper={children => (
            <CustomTooltip
              tooltipStyles={{ position: 'relative', top: theme.spacing(12) }}
              placement="top"
              title={
                <>
                  <div className={classes.tooltipTitle}>{t('Peer group comparison')}</div>
                  <div>{best && `${t('Best')}: ${best}`}</div>
                  <div>{average && `${t('Average')}: ${average}`}</div>
                  <div>{worst && `${t('Worst')}: ${worst}`}</div>
                </>
              }
              arrow
            >
              {children}
            </CustomTooltip>
          )}
        >
          <div>
            <Doughnut data={data} options={options} />
            <div className={classes.scoreNumbers}>
              <Typography className={classes.scoreNumber} variant="h3">
                {prepareDataToDisplay(score)}
              </Typography>
              <Typography className={classes.scoreMaxNumber} variant="h3">
                /{maxScore}
              </Typography>
            </div>
          </div>
        </ConditionalWrapper>
      </div>
    </>
  );
};

ESGScore.defaultProps = {
  title: null,
  score: 0,
  worst: null,
  average: null,
  best: null,
};

ESGScore.propTypes = {
  title: PropTypes.string,
  score: (props, propName, componentName) => {
    const prop = props[propName];

    if (typeof prop !== 'number')
      throw new Error(`${componentName} only accepts numbers for ${propName} prop`);

    if (prop > 10 || prop < 1)
      throw new Error(
        `${componentName} only accepts numbers between 1 and 10 for ${propName} prop`,
      );

    return undefined;
  },
  worst: PropTypes.number,
  average: PropTypes.number,
  best: PropTypes.number,
};

export default ESGScore;
